/**
 * FO-443 (batch-60 facturen-verwijderen) - DE pure regel "mag deze factuur
 * verwijderd worden, en wat gebeurt er dan".
 *
 * Verwijderbaar = alles zonder definitief fiscaal nummer OF nooit verzonden,
 * origin-bewust:
 *   - eigen concepten (geen fiscaal nummer)            -> ja, reden optioneel
 *   - eigen factuur met nummer, nooit verzonden        -> ja, nummer wordt een
 *     bewust reeks-gat (FO-421); reden verplicht
 *   - eigen factuur met nummer EN verzonden            -> nee: crediteren
 *   - interne Finqle-proforma (oud)                    -> ja, reden verplicht;
 *     batch-63 (FO-470): de gegevens gaan naar het facturatieverzoek en de
 *     werkbon blijft "gefactureerd via Finqle"
 *   - Finqle UBL-retour-import, niet verwerkt          -> ja, reden verplicht
 *   - gespiegelde Finqle-API-factuur, niet definitief  -> ja, reden verplicht
 *   - Finqle-definitief (API-status vanaf akkoord)     -> nee: corrigeer bij Finqle
 *   - al verwerkt in de boeken (betaald / geëxporteerd / naar Zenvoices) -> nee
 *
 * batch-63 (FO-471 O6): een reeksnummer blijft op de verwijderde rij staan en
 * wordt altijd een bewust gat, ongeacht of de BV-code klopt; andere nummers
 * komen vrij.
 *
 * Gedeeld door de API-route (409/422), de lijst-/detail-DTO (`verwijderbaarheid`)
 * en de bulk-actie, zodat knop, dialoog en server nooit uiteenlopen. Geen I/O.
 */

import {
  FINQLE_INVOICE_DEFINITIEVE_STATUSSEN,
  FINQLE_INVOICE_STATUS_LABELS,
  type FactuurVerwijderBlokkade,
  type FactuurVerwijderSoort,
  type FactuurVerwijderbaarheidDTO,
  type FactuurVerwijderbaarheidInput,
  type FinqleInvoiceStatusKey,
} from '@streep/types'

/**
 * Eigen verkoopreeks-formaat `FA{jaar}{bvCode}{volgnr}` (plus het oude
 * streepjes-formaat van dezelfde reeks). Spiegelt `FA_NUMMER_RE` in
 * `apps/api/src/lib/invoice-numbers.ts`; alleen deze nummers worden bij
 * verwijderen een bewust gat in de FO-421-controle.
 */
const EIGEN_REEKS_RE = /^FA(\d{4})-?([0-9A-Za-z])(\d{4,9})$/

export interface EigenReeksNummer {
  year: number
  bvCode: string
  volgnummer: number
}

export function parseEigenReeksNummer(nummer: string | null | undefined): EigenReeksNummer | null {
  if (nummer == null) return null
  const m = EIGEN_REEKS_RE.exec(nummer.trim())
  if (!m) return null
  return { year: Number(m[1]), bvCode: m[2]!, volgnummer: Number(m[3]) }
}

export function isEigenReeksNummer(nummer: string | null | undefined): boolean {
  return parseEigenReeksNummer(nummer) !== null
}

/**
 * Wordt dit nummer bij verwijderen een bewust gat? batch-63 (FO-471 O6): elk
 * reeksnummer, ongeacht de BV-code - het nummer blijft op de verwijderde rij
 * staan en de reeksbewaking legt het vast als GEACCEPTEERD gat (bij een
 * afwijkende code in de reeks waar die code bij hoort). De BV-code bepaalt
 * alleen nog de uitleg (`nummerGevolg`).
 */
export function isBewustGatNummer(
  nummer: string | null | undefined,
  _entityInvoiceBvCode?: string | null,
): boolean {
  return parseEigenReeksNummer(nummer) !== null
}

/**
 * Vaste reden voor het FO-421-register bij een verwijderde factuur met nummer.
 * (De naam is historisch; batch-63 geldt het voor elke verwijderde factuur.)
 */
export const VERWIJDERD_CONCEPT_MET_NUMMER_REDEN = 'Factuur verwijderd'

function gevuld(waarde: string | Date | null | undefined): boolean {
  if (waarde == null) return false
  if (waarde instanceof Date) return !Number.isNaN(waarde.getTime())
  return waarde.trim() !== ''
}

function uniek(items: string[]): string[] {
  return [...new Set(items)]
}

/** Kanalen waarlangs een uitgegeven factuur al de deur uit is. */
function verzendSignalen(f: FactuurVerwijderbaarheidInput): string[] {
  const via: string[] = []
  if (f.status === 'VERZONDEN') via.push('gemarkeerd als verzonden')
  if (f.status === 'BETAALD' || gevuld(f.betaaldOp)) via.push('betaald')
  if (f.status === 'GEFACTUREERD') via.push('gefactureerd')
  if (
    f.status === 'VERZONDEN_NAAR_FINQLE' ||
    f.status === 'IN_BEHANDELING_FINQLE' ||
    gevuld(f.verzondenNaarFinqleOp)
  ) {
    via.push('naar Finqle verstuurd')
  }
  if (gevuld(f.pdfVerzondenOp)) via.push('PDF verzonden')
  if (gevuld(f.zenvoicesXmlVerzondenOp)) via.push('UBL naar Zenvoices')
  if (gevuld(f.verzondenNaarAdminOp)) via.push('naar de administratie')
  if (gevuld(f.exportedAt)) via.push('geëxporteerd naar de boekhouding')
  if (f.portaalZichtbaar) via.push('zichtbaar in het klantportaal')
  return uniek(via)
}

/**
 * Harde verwerkingssignalen voor een Finqle-stuk (import, spiegel, proforma):
 * daarna is het in de boeken en corrigeer je bij Finqle of in de boekhouding.
 */
function verwerkingsSignalen(f: FactuurVerwijderbaarheidInput): string[] {
  const via: string[] = []
  if (f.status === 'BETAALD' || gevuld(f.betaaldOp)) via.push('betaald')
  if (gevuld(f.zenvoicesXmlVerzondenOp)) via.push('UBL naar Zenvoices')
  if (gevuld(f.exportedAt)) via.push('geëxporteerd naar de boekhouding')
  return uniek(via)
}

function blokkade(
  code: FactuurVerwijderBlokkade,
  uitleg: string,
  verzondenVia: string[] = [],
): FactuurVerwijderbaarheidDTO {
  return {
    kan: false,
    soort: null,
    blokkade: code,
    redenVerplicht: false,
    uitleg,
    gevolgen: [],
    waarschuwingen: [],
    verzondenVia,
  }
}

function toegestaan(
  soort: FactuurVerwijderSoort,
  redenVerplicht: boolean,
  uitleg: string,
  gevolgen: string[],
  waarschuwingen: string[] = [],
  verzondenVia: string[] = [],
): FactuurVerwijderbaarheidDTO {
  return {
    kan: true,
    soort,
    blokkade: null,
    redenVerplicht,
    uitleg,
    gevolgen,
    waarschuwingen,
    verzondenVia,
  }
}

function werkbonGevolg(aantal: number): string[] {
  if (aantal <= 0) return []
  return [
    aantal === 1
      ? 'De gekoppelde werkbon wordt losgekoppeld en is weer facturabel'
      : `De ${aantal} gekoppelde werkbonnen worden losgekoppeld en zijn weer facturabel`,
  ]
}

/** Wat er met een nummer gebeurt: reeksnummer = bewust gat en blijft staan; anders vrij. */
function nummerGevolg(nummer: string, entityInvoiceBvCode: string | null): string {
  const reeks = parseEigenReeksNummer(nummer)
  if (!reeks) {
    return `Nummer ${nummer} hoort niet bij een bewaakte nummerreeks en komt vrij`
  }
  const waar =
    entityInvoiceBvCode && reeks.bvCode !== entityInvoiceBvCode
      ? `in de reeks met BV-code ${reeks.bvCode} (wijkt af van deze BV)`
      : 'in de nummerreeks'
  return `Nummer ${nummer} blijft op de verwijderde factuur staan en wordt een bewust gat ${waar}, met reden "${VERWIJDERD_CONCEPT_MET_NUMMER_REDEN}" (zichtbaar in de gaten-controle); het wordt nooit opnieuw uitgegeven`
}

const HERSTELBAAR = 'De factuur blijft herstelbaar via het filter "Verwijderde facturen"'
const PORTAAL_WAARSCHUWING =
  'Deze factuur is zichtbaar in het klantportaal; na verwijderen ziet de klant hem niet meer'

/**
 * Bepaal de verwijderbaarheid van één factuur. Zuiver (geen I/O); de API vult
 * de invoer uit de Prisma-rij, de UI leest het resultaat uit de DTO.
 */
export function bepaalFactuurVerwijderbaarheid(
  f: FactuurVerwijderbaarheidInput,
): FactuurVerwijderbaarheidDTO {
  if (gevuld(f.deletedAt)) {
    return blokkade('AL_VERWIJDERD', 'Deze factuur is al verwijderd.')
  }

  // Finqle-definitief (spec 143 par. 6.3): geldt voor elke rij met een
  // API-koppeling, ook een geadopteerde import. Nooit "wegwerken".
  const finqleStatus = f.finqleApiStatus?.trim().toUpperCase() ?? null
  const finqleDefinitief =
    f.finqleApiInvoiceId != null &&
    ((finqleStatus != null &&
      (FINQLE_INVOICE_DEFINITIEVE_STATUSSEN as readonly string[]).includes(finqleStatus)) ||
      gevuld(f.finqleApiBetaaldDoorKlantOp) ||
      gevuld(f.finqleApiBetaaldAanStreepOp))
  if (finqleDefinitief) {
    const label =
      finqleStatus && finqleStatus in FINQLE_INVOICE_STATUS_LABELS
        ? FINQLE_INVOICE_STATUS_LABELS[finqleStatus as FinqleInvoiceStatusKey]
        : (finqleStatus ?? 'betaald')
    return blokkade(
      'FINQLE_DEFINITIEF',
      `Dit is bij Finqle een definitieve factuur (Finqle-status: ${label}); corrigeer bij Finqle. STREEP werkt een Finqle-factuur niet weg.`,
    )
  }

  const nummer = f.invoiceNumber?.trim() || null

  // Interne Finqle-proforma: admin/rapportage zonder echt STREEP-nummer.
  if (f.isFinqleProforma) {
    const verwerkt = verwerkingsSignalen(f)
    if (verwerkt.length > 0) {
      return blokkade(
        'DEFINITIEF_VERZONDEN',
        `Deze proforma is al verwerkt (${verwerkt.join(', ')}); corrigeer in de boekhouding. Verwijderen kan niet meer.`,
        verwerkt,
      )
    }
    return toegestaan(
      'FINQLE_PROFORMA',
      true,
      'Oude interne Finqle-proforma (geen fiscale factuur); verwijderen kan met reden.',
      [
        'De interne proforma wordt verwijderd; de gegevens (bedrag, verzenddatum, werkbonnen) staan voortaan op het facturatieverzoek en Finqle factureert de werkbon zelf naar de klant',
        ...(f.werkbonCount > 0
          ? [
              f.werkbonCount === 1
                ? 'De werkbon wordt losgekoppeld van de proforma en blijft gemarkeerd als "gefactureerd via Finqle"'
                : `De ${f.werkbonCount} werkbonnen worden losgekoppeld van de proforma en blijven gemarkeerd als "gefactureerd via Finqle"`,
            ]
          : []),
        'De definitieve Finqle-factuur komt via de mailbox of een upload binnen en wordt aan het facturatieverzoek gekoppeld',
        HERSTELBAAR,
      ],
    )
  }

  // Gespiegelde Finqle-API-factuur die bij Finqle nog niet definitief is.
  if (f.origin === 'FINQLE_API') {
    const verwerkt = verwerkingsSignalen(f)
    if (verwerkt.length > 0) {
      return blokkade(
        'DEFINITIEF_VERZONDEN',
        `Deze gespiegelde Finqle-factuur is al verwerkt (${verwerkt.join(', ')}); corrigeer bij Finqle of in de boekhouding. Verwijderen kan niet meer.`,
        verwerkt,
      )
    }
    return toegestaan(
      'FINQLE_SPIEGEL',
      true,
      'Gespiegelde Finqle-factuur die bij Finqle nog niet definitief is; verwijderen kan met reden.',
      [
        'De gespiegelde Finqle-factuur wordt verwijderd; de Finqle-gegevens blijven op het facturatieverzoek staan',
        ...(nummer ? [nummerGevolg(nummer, f.entityInvoiceBvCode)] : []),
        ...(f.billingRequestCount > 0
          ? [
              f.billingRequestCount === 1
                ? 'Het gekoppelde facturatieverzoek wordt losgekoppeld'
                : `De ${f.billingRequestCount} gekoppelde facturatieverzoeken worden losgekoppeld`,
            ]
          : []),
        ...werkbonGevolg(f.werkbonCount),
        'De Finqle-sync en -webhook maken dit stuk niet opnieuw aan zolang de genegeerd-marker actief is (opheffen bij Facturatieverzoeken (Finqle))',
        HERSTELBAAR,
      ],
      f.portaalZichtbaar ? [PORTAAL_WAARSCHUWING] : [],
    )
  }

  if (f.origin === 'IMPORT') {
    if (f.isFinqleImport) {
      const verwerkt = verwerkingsSignalen(f)
      if (verwerkt.length > 0) {
        return blokkade(
          'DEFINITIEF_VERZONDEN',
          `Deze Finqle-factuur is al verwerkt (${verwerkt.join(', ')}). Corrigeer bij Finqle of in de boekhouding; verwijderen kan niet meer.`,
          verwerkt,
        )
      }
      return toegestaan(
        'FINQLE_IMPORT',
        true,
        'Geïmporteerde Finqle-factuur (UBL-retour) die nog niet verwerkt is; verwijderen kan met reden.',
        [
          'De geïmporteerde factuur wordt verwijderd; de opgeslagen XML en PDF blijven bewaard',
          ...(nummer ? [nummerGevolg(nummer, f.entityInvoiceBvCode)] : []),
          ...werkbonGevolg(f.werkbonCount),
          ...(nummer
            ? [
                `Een nieuwe import van factuurnummer ${nummer} wordt genegeerd zolang de marker actief is (opheffen bij Facturatieverzoeken (Finqle))`,
              ]
            : []),
          HERSTELBAAR,
        ],
        f.portaalZichtbaar ? [PORTAAL_WAARSCHUWING] : [],
      )
    }
    const via = verzendSignalen(f)
    if (via.length > 0) {
      return blokkade(
        'DEFINITIEF_VERZONDEN',
        `Deze geïmporteerde factuur is al verwerkt (${via.join(', ')}). Verwijderen kan niet meer; corrigeer via een creditnota.`,
        via,
      )
    }
    return toegestaan(
      'IMPORT',
      true,
      'Geïmporteerde factuur die nog niet verwerkt is; verwijderen kan met reden.',
      [
        'De geïmporteerde factuur wordt verwijderd',
        ...(nummer ? [nummerGevolg(nummer, f.entityInvoiceBvCode)] : []),
        ...werkbonGevolg(f.werkbonCount),
        HERSTELBAAR,
      ],
    )
  }

  // Eigen facturen (werkbon / offerte / handmatig / creditnota).
  const via = verzendSignalen(f)
  // Vergrendeld (export-batch, reconcile) telt voor een eigen factuur ook als
  // "de deur uit"; imports zijn altijd vergrendeld en vallen hierbuiten.
  if (f.isLocked && nummer) via.push('vergrendeld (geëxporteerd of gefactureerd)')
  if (nummer) {
    if (via.length > 0) {
      const soortTekst = f.isCredit ? 'creditnota' : 'factuur'
      const correctie = f.isCredit
        ? 'corrigeer met een nieuwe factuur'
        : 'maak een creditnota om te corrigeren'
      return blokkade(
        'DEFINITIEF_VERZONDEN',
        `Definitieve ${soortTekst} ${nummer} is al verzonden (${via.join(', ')}). Verwijderen kan niet meer; ${correctie}.`,
        via,
      )
    }
    return toegestaan(
      'EIGEN_MET_NUMMER',
      true,
      `${f.isCredit ? 'Creditnota' : 'Factuur'} ${nummer} heeft een definitief nummer maar is nooit verzonden; verwijderen kan met reden.`,
      [nummerGevolg(nummer, f.entityInvoiceBvCode), ...werkbonGevolg(f.werkbonCount), HERSTELBAAR],
      gevuld(f.verzondenNaarKlantOp)
        ? ['De klant heeft het concept eerder ter accordering ontvangen']
        : [],
    )
  }

  // Concept zonder fiscaal nummer.
  const waarschuwingen: string[] = []
  if (
    f.status === 'VERZONDEN_NAAR_FINQLE' ||
    f.status === 'IN_BEHANDELING_FINQLE' ||
    gevuld(f.verzondenNaarFinqleOp)
  ) {
    waarschuwingen.push(
      'Dit concept is al naar Finqle verstuurd; komt de Finqle-retour binnen, dan wordt die als losse import geboekt',
    )
  }
  if (
    f.status === 'VERSTUURD_KLANT' ||
    f.status === 'AKKOORD_KLANT' ||
    gevuld(f.verzondenNaarKlantOp)
  ) {
    waarschuwingen.push('De klant heeft dit concept ter accordering ontvangen')
  }
  return toegestaan(
    'EIGEN_CONCEPT',
    waarschuwingen.length > 0,
    'Concept zonder fiscaal nummer; verwijderen kan.',
    [...werkbonGevolg(f.werkbonCount), HERSTELBAAR],
    waarschuwingen,
  )
}
